import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../ui/activqms-loader";
import {
  GET_LINKEDIN_OAUTH_STATUS,
  INITIATE_LINKEDIN_OAUTH,
  DISCONNECT_LINKEDIN_ACCOUNT,
  LIST_LINKEDIN_AD_ACCOUNTS,
  UPDATE_LINKEDIN_AD_ACCOUNT,
  LinkedInConnectionStatus,
  openLinkedInOAuthPopup,
  formatLinkedInDate,
} from "../../services/linkedin-api";
import { useTenantContext } from "../../hooks/use-tenant-context";

interface LinkedInConfigurationProps {
  clientId?: string; // Optional - will use tenant context if not provided
  onConfigurationChange?: () => void;
}

export function LinkedInConfiguration({
  clientId,
  onConfigurationChange,
}: LinkedInConfigurationProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionSuccess, setConnectionSuccess] = useState<string | null>(null);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [isChangingAccount, setIsChangingAccount] = useState(false);

  // Get tenant context
  const { tenantId: contextTenantId } = useTenantContext();
  const tenantId = clientId || contextTenantId;

  // Query connection status
  const {
    data: statusData,
    loading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery(GET_LINKEDIN_OAUTH_STATUS, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // OAuth initiation mutation
  const [initiateOAuth] = useMutation(INITIATE_LINKEDIN_OAUTH, {
    onCompleted: async (data) => {
      if (data.initiateLinkedInOAuth.success && data.initiateLinkedInOAuth.authUrl) {
        try {
          // Clear any previous messages
          setConnectionError(null);
          setConnectionSuccess(null);

          // Open OAuth URL in popup window and wait for completion
          const success = await openLinkedInOAuthPopup(
            data.initiateLinkedInOAuth.authUrl
          );
          setIsConnecting(false);

          if (success) {
            // Wait a moment for the backend to process the OAuth callback
            setTimeout(async () => {
              await refetchStatus();
              const updatedStatus = await refetchStatus();

              if (updatedStatus.data?.getLinkedInOAuthStatus?.isConnected) {
                setConnectionSuccess(
                  `Successfully connected to LinkedIn Ads account!`
                );
                onConfigurationChange?.();
              } else {
                setConnectionError(
                  "Connection completed but status not updated. Please refresh the page."
                );
              }
            }, 2000);
          } else {
            setConnectionError(
              "OAuth flow was cancelled or failed. Please try again."
            );
          }
        } catch (error) {
          setIsConnecting(false);
          setConnectionError(
            "An error occurred during the connection process. Please try again."
          );
          console.error("OAuth popup error:", error);
        }
      } else {
        setIsConnecting(false);
        setConnectionError(
          data.initiateLinkedInOAuth.error || "Failed to initiate OAuth flow"
        );
      }
    },
    onError: (error) => {
      setIsConnecting(false);
      setConnectionError(
        `Failed to initiate OAuth: ${error.message}`
      );
    },
  });

  // Disconnect mutation
  const [disconnectAccount] = useMutation(DISCONNECT_LINKEDIN_ACCOUNT, {
    onCompleted: async (data) => {
      setIsDisconnecting(false);
      if (data.disconnectLinkedInAccount) {
        setConnectionSuccess("Successfully disconnected from LinkedIn Ads");
        await refetchStatus();
        onConfigurationChange?.();
      } else {
        setConnectionError("Failed to disconnect from LinkedIn Ads");
      }
    },
    onError: (error) => {
      setIsDisconnecting(false);
      setConnectionError(`Failed to disconnect: ${error.message}`);
    },
  });

  // List ad accounts query (lazy - only fetch when needed)
  const {
    data: accountsData,
    loading: accountsLoading,
    refetch: refetchAccounts,
  } = useQuery(LIST_LINKEDIN_AD_ACCOUNTS, {
    skip: !showAccountSelector, // Only fetch when selector is shown
    fetchPolicy: "network-only",
  });

  // Update ad account mutation
  const [updateAdAccount] = useMutation(UPDATE_LINKEDIN_AD_ACCOUNT, {
    onCompleted: async () => {
      setIsChangingAccount(false);
      setShowAccountSelector(false);
      setConnectionSuccess("Successfully updated LinkedIn Ad Account");
      await refetchStatus();
      onConfigurationChange?.();
    },
    onError: (error) => {
      setIsChangingAccount(false);
      setConnectionError(`Failed to update account: ${error.message}`);
    },
  });

  const handleConnect = () => {
    setIsConnecting(true);
    setConnectionError(null);
    setConnectionSuccess(null);
    initiateOAuth({
      variables: {
        input: {
          redirectUrl: `${window.location.origin}/linkedin/oauth/callback`,
        },
      },
    });
  };

  const handleDisconnect = () => {
    if (
      window.confirm(
        "Are you sure you want to disconnect from LinkedIn Ads? This will disable conversion tracking for all conversations."
      )
    ) {
      setIsDisconnecting(true);
      setConnectionError(null);
      setConnectionSuccess(null);
      disconnectAccount();
    }
  };

  const handleChangeAccount = () => {
    setConnectionError(null);
    setConnectionSuccess(null);
    setShowAccountSelector(true);
    refetchAccounts();
  };

  const handleSelectAccount = (accountId: string, accountName: string) => {
    setIsChangingAccount(true);
    setConnectionError(null);
    setConnectionSuccess(null);
    updateAdAccount({
      variables: {
        input: {
          adAccountId: accountId,
          adAccountName: accountName,
        },
      },
    });
  };

  const status: LinkedInConnectionStatus | undefined =
    statusData?.getLinkedInOAuthStatus;

  if (statusLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <ActivQMSLoader size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-8 w-8 text-[#0A66C2]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              LinkedIn Ads Integration
            </h3>
            <p className="text-sm text-gray-600">
              Track conversions from your activQMS conversations in LinkedIn Campaign Manager
            </p>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="mb-6">
        {status?.isConnected ? (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Connected</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-gray-500">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Not Connected</span>
          </div>
        )}
      </div>

      {/* Connection Details */}
      {status?.isConnected && (
        <div className="mb-6 space-y-2 text-sm">
          {status.adAccountName && (
            <div className="flex justify-between">
              <span className="text-gray-600">Ad Account:</span>
              <span className="font-medium text-gray-900">{status.adAccountName}</span>
            </div>
          )}
          {status.adAccountId && (
            <div className="flex justify-between">
              <span className="text-gray-600">Account ID:</span>
              <span className="font-mono text-xs text-gray-900">{status.adAccountId}</span>
            </div>
          )}
          {status.connectedAt && (
            <div className="flex justify-between">
              <span className="text-gray-600">Connected:</span>
              <span className="text-gray-900">{formatLinkedInDate(status.connectedAt)}</span>
            </div>
          )}
          {status.expiresAt && (
            <div className="flex justify-between">
              <span className="text-gray-600">Token Expires:</span>
              <span className="text-gray-900">{formatLinkedInDate(status.expiresAt)}</span>
            </div>
          )}
        </div>
      )}

      {/* Error/Success Messages */}
      {connectionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{connectionError}</p>
        </div>
      )}

      {connectionSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{connectionSuccess}</p>
        </div>
      )}

      {statusError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            Unable to load connection status. Please try refreshing the page.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        {!status?.isConnected ? (
          <button
            onClick={handleConnect}
            disabled={isConnecting || !tenantId}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#0A66C2] hover:bg-[#004182] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0A66C2] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? (
              <>
                <ActivQMSLoader size="sm" className="mr-2" />
                Connecting...
              </>
            ) : (
              <>
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                Connect LinkedIn Ads
              </>
            )}
          </button>
        ) : (
          <>
            <button
              onClick={handleChangeAccount}
              disabled={isChangingAccount || showAccountSelector}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Change Account
            </button>
            <button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDisconnecting ? (
                <>
                  <ActivQMSLoader size="sm" className="mr-2" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect"
              )}
            </button>
          </>
        )}
      </div>

      {/* Account Selector */}
      {showAccountSelector && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Select LinkedIn Ad Account</h4>
          {accountsLoading ? (
            <div className="flex items-center justify-center py-4">
              <ActivQMSLoader size="sm" />
              <span className="ml-2 text-sm text-gray-600">Loading accounts...</span>
            </div>
          ) : accountsData?.listLinkedInAdAccounts && accountsData.listLinkedInAdAccounts.length > 0 ? (
            <div className="space-y-2">
              {accountsData.listLinkedInAdAccounts.map((account: any) => (
                <button
                  key={account.id}
                  onClick={() => handleSelectAccount(account.id, account.name)}
                  disabled={isChangingAccount || account.id === status?.adAccountId}
                  className={`w-full text-left px-4 py-3 border rounded-md transition-colors ${
                    account.id === status?.adAccountId
                      ? "border-green-500 bg-green-50 cursor-default"
                      : "border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{account.name}</div>
                      <div className="text-xs text-gray-500">ID: {account.id}</div>
                      {account.currency && (
                        <div className="text-xs text-gray-500">Currency: {account.currency}</div>
                      )}
                    </div>
                    {account.id === status?.adAccountId && (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                </button>
              ))}
              <button
                onClick={() => setShowAccountSelector(false)}
                className="w-full mt-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              No ad accounts found. Please make sure you have admin access to at least one LinkedIn Ad Account.
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          <strong>Note:</strong> After connecting, you can configure conversion tracking for individual conversations in the conversation settings.
        </p>
      </div>
    </div>
  );
}

